import {
  createMassTortCustomFieldV1,
  createMassTortV1,
  deleteMassTortCustomFieldV1,
  deleteMassTortV1,
  generateMassTortMatterTemplateDocumentsV1,
  retrieveMassTortMatterSearchV1,
  retrieveMassTortSearchV1,
  retrieveMassTortTemplateGenerationRequestV1,
  retrieveMassTortV3,
  updateMassTortCustomFieldsV1,
  updateMassTortMatterCustomFieldsV1,
  updateMassTortV1,
  type RetrieveMassTortMatterSearchInput,
  type RetrieveMassTortTemplateGenerationRequestV1Input,
} from "@/lib/data/requests";
import {
  retryFailedMassTortTemplateGenerationJobsV1,
  retrieveMassTortTemplateGenerationRequestsV1,
  type RetrieveMassTortTemplateGenerationRequestsV1Input,
  type RetryFailedMassTortTemplateGenerationJobsV1Input,
} from "@/lib/data/requests/mass-torts/mass-tort";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

export const retrieveMassTortQueryOptions = (
  massTortId: Parameters<typeof retrieveMassTortV3>[0],
) => ({
  queryKey: [queryKeys.massTorts, massTortId],
  enabled: !!massTortId,
  queryFn: async () => retrieveMassTortV3(massTortId),
  fetchOnWindowFocus: false,
});

export const retrieveMassTortSearchQueryOptions = () => ({
  queryKey: [queryKeys.massTorts, "search"],
  queryFn: () => retrieveMassTortSearchV1(),
});

export const retrieveMassTortMatterSearchQueryOptions = (
  body: RetrieveMassTortMatterSearchInput,
) => ({
  queryKey: [queryKeys.massTorts, "matter-search", body],
  queryFn: () => retrieveMassTortMatterSearchV1(body),
  enabled: Boolean(body.massTortId),
});

export const retrieveMassTortTemplateGenerationRequestQueryOptions = (
  params: RetrieveMassTortTemplateGenerationRequestV1Input,
) => ({
  queryKey: [
    queryKeys.massTorts,
    params.massTortId,
    "mass-tort-template-generation",
    params.requestId,
  ],
  queryFn: () => retrieveMassTortTemplateGenerationRequestV1(params),
  enabled: Boolean(params.massTortId && params.requestId),
});

export const retrieveMassTortTemplateGenerationRequestsQueryOptions = (
  params: RetrieveMassTortTemplateGenerationRequestsV1Input,
) => ({
  queryKey: [
    queryKeys.massTorts,
    params.massTortId,
    "mass-tort-template-generation",
  ],
  queryFn: () => retrieveMassTortTemplateGenerationRequestsV1(params),
  enabled: Boolean(params.massTortId),
});

const invalidateMassTortQueries = async (
  queryClient: ReturnType<typeof useQueryClient>,
  massTortId?: string,
) => {
  if (massTortId) {
    await queryClient.invalidateQueries({
      queryKey: [queryKeys.massTorts, massTortId],
    });
  }
  await queryClient.invalidateQueries({ queryKey: [queryKeys.massTorts] });
};

export const useCreateMassTort = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMassTortV1>[0]) =>
      createMassTortV1(data),
    onError: () => {
      toast.error("Error creating mass tort");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.massTorts] });
    },
    onSuccess: () => {
      toast.success("Mass tort created successfully");
    },
  });
};

type UseUpdateMassTortOptions = {
  successToast?: string | null;
};

export const useUpdateMassTort = ({
  successToast = "Mass tort updated successfully",
}: UseUpdateMassTortOptions = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMassTortV1>[0]) =>
      updateMassTortV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error updating mass tort");
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.massTorts, variables.id],
      });
      await queryClient.invalidateQueries({ queryKey: [queryKeys.massTorts] });
    },
    onSuccess: () => {
      if (successToast) {
        toast.success(successToast);
      }
    },
  });
};

export const useDeleteMassTort = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMassTortV1>[0]) =>
      deleteMassTortV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error deleting mass tort");
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.massTorts, variables.massTortId],
      });
      await queryClient.invalidateQueries({ queryKey: [queryKeys.massTorts] });
    },
    onSuccess: () => {
      toast.success("Mass tort deleted successfully");
    },
  });
};

export const useGenerateMassTortMatterTemplateDocuments = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Parameters<typeof generateMassTortMatterTemplateDocumentsV1>[0],
    ) => generateMassTortMatterTemplateDocumentsV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(
        error,
        "Error starting mass tort template generation",
      );
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateMassTortQueries(queryClient, variables.massTortId);
    },
    onSuccess: () => {
      toast.success("Mass tort template generation started successfully");
    },
  });
};

export const useRetryFailedMassTortTemplateGenerationJobs = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RetryFailedMassTortTemplateGenerationJobsV1Input) =>
      retryFailedMassTortTemplateGenerationJobsV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(
        error,
        "Error retrying failed mass tort template generation jobs",
      );
    },
    onSettled: async (_data, _error, variables) => {
      await Promise.all(
        variables.requests.map(({ massTortId }) =>
          invalidateMassTortQueries(queryClient, massTortId),
        ),
      );
    },
    onSuccess: () => {
      toast.success("Failed mass tort template generation jobs retried");
    },
  });
};

export const useCreateMassTortCustomField = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMassTortCustomFieldV1>[0]) =>
      createMassTortCustomFieldV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error creating mass tort custom field");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateMassTortQueries(queryClient, variables.massTortId);
    },
    onSuccess: () => {
      toast.success("Mass tort custom field created successfully");
    },
  });
};

type UseUpdateMassTortCustomFieldsOptions = {
  successToast?: string | null;
};

export const useUpdateMassTortCustomFields = ({
  successToast = "Mass tort custom fields updated successfully",
}: UseUpdateMassTortCustomFieldsOptions = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMassTortCustomFieldsV1>[0]) =>
      updateMassTortCustomFieldsV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error updating mass tort custom fields");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateMassTortQueries(queryClient, variables.massTortId);
    },
    onSuccess: () => {
      if (successToast) {
        toast.success(successToast);
      }
    },
  });
};

type UseUpdateMassTortMatterCustomFieldsOptions = {
  successToast?: string | null;
};

export const useUpdateMassTortMatterCustomFields = ({
  successToast = "Mass tort matter custom field updated successfully",
}: UseUpdateMassTortMatterCustomFieldsOptions = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Parameters<typeof updateMassTortMatterCustomFieldsV1>[0],
    ) => updateMassTortMatterCustomFieldsV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(
        error,
        "Error updating mass tort matter custom field",
      );
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: () => {
      if (successToast) {
        toast.success(successToast);
      }
    },
  });
};

export const useDeleteMassTortCustomField = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMassTortCustomFieldV1>[0]) =>
      deleteMassTortCustomFieldV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error deleting mass tort custom field");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateMassTortQueries(queryClient, variables.massTortId);
    },
    onSuccess: () => {
      toast.success("Mass tort custom field deleted successfully");
    },
  });
};
